import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  MousePointer,
  Eye,
  ArrowRight,
  RefreshCw,
  Calendar,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminAnalytics = () => {
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [summaryRes, funnelRes, eventsRes] = await Promise.all([
        axios.get(`/api/analytics/summary?days=${dateRange}`, { headers }),
        axios.get(`/api/analytics/funnel?days=${dateRange}`, { headers }),
        axios.get(`/api/analytics/events?limit=20`, { headers })
      ]);
      
      setSummary(summaryRes.data);
      setFunnel(funnelRes.data);
      setRecentEvents(eventsRes.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatEventType = (type) => {
    return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const formatTimestamp = (ts) => {
    const date = new Date(ts);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1B2FFF]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-red-400">Error loading analytics: {error}</p>
        <Button onClick={fetchAnalytics} variant="outline" className="border-[#C7CCD8]/30 text-[#C7CCD8]">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Analytics Dashboard</h2>
          <p className="text-[#C7CCD8]">Track visitor activity and conversions</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-36 bg-white/5 border-[#1B2FFF]/30 text-white">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-[#1B2FFF]/30">
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchAnalytics} variant="outline" size="icon" className="border-[#C7CCD8]/30 text-[#C7CCD8] hover:bg-white/5">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6 bg-white/5 border-[#1B2FFF]/20">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#1B2FFF]/20 flex items-center justify-center">
              <Eye className="w-6 h-6 text-[#1B2FFF]" />
            </div>
            <div>
              <p className="text-sm text-[#C7CCD8]">Page Views</p>
              <p className="text-2xl font-bold text-white">{summary?.total_page_views || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white/5 border-[#1B2FFF]/20">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#2CB1A1]/20 flex items-center justify-center">
              <MousePointer className="w-6 h-6 text-[#2CB1A1]" />
            </div>
            <div>
              <p className="text-sm text-[#C7CCD8]">Total Events</p>
              <p className="text-2xl font-bold text-white">{summary?.total_events || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white/5 border-[#1B2FFF]/20">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#1B2FFF]/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-[#1B2FFF]" />
            </div>
            <div>
              <p className="text-sm text-[#C7CCD8]">Unique Sessions</p>
              <p className="text-2xl font-bold text-white">{summary?.unique_sessions || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white/5 border-[#1B2FFF]/20">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#2CB1A1]/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-[#2CB1A1]" />
            </div>
            <div>
              <p className="text-sm text-[#C7CCD8]">Conversion Rate</p>
              <p className="text-2xl font-bold text-white">{funnel?.conversion_rate || 0}%</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Conversion Funnel */}
        <Card className="p-6 bg-white/5 border-[#1B2FFF]/20">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#1B2FFF]" />
            Conversion Funnel
          </h3>
          <div className="space-y-3">
            {funnel?.funnel_stages?.map((stage, index) => {
              const maxCount = Math.max(...(funnel?.funnel_stages?.map(s => s.count) || [1]));
              const percentage = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
              
              return (
                <div key={stage.stage} className="flex items-center gap-3">
                  <div className="w-24 text-sm text-[#C7CCD8]">{stage.stage}</div>
                  <div className="flex-1 h-8 bg-white/5 rounded-lg overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#1B2FFF] to-[#2CB1A1] rounded-lg flex items-center justify-end pr-2 transition-all duration-500"
                      style={{ width: `${Math.max(percentage, 5)}%` }}
                    >
                      <span className="text-xs font-medium text-white">{stage.count}</span>
                    </div>
                  </div>
                  {index < (funnel?.funnel_stages?.length || 0) - 1 && (
                    <ArrowRight className="w-4 h-4 text-[#C7CCD8]/50" />
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Daily Activity */}
        <Card className="p-6 bg-white/5 border-[#1B2FFF]/20">
          <h3 className="font-semibold text-white mb-4">Daily Activity</h3>
          <div className="space-y-2">
            {summary?.daily_activity?.map((day) => {
              const maxEvents = Math.max(...(summary?.daily_activity?.map(d => d.events) || [1]));
              const percentage = maxEvents > 0 ? (day.events / maxEvents) * 100 : 0;
              
              return (
                <div key={day.date} className="flex items-center gap-3">
                  <div className="w-20 text-sm text-[#C7CCD8]">
                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                  <div className="flex-1 h-6 bg-white/5 rounded overflow-hidden">
                    <div 
                      className="h-full bg-[#1B2FFF] rounded transition-all duration-500"
                      style={{ width: `${Math.max(percentage, 2)}%` }}
                    />
                  </div>
                  <div className="w-16 text-right text-sm">
                    <span className="text-white font-medium">{day.events}</span>
                    <span className="text-[#C7CCD8] ml-1">events</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <Card className="p-6 bg-white/5 border-[#1B2FFF]/20">
          <h3 className="font-semibold text-white mb-4">Top Pages</h3>
          <div className="space-y-2">
            {summary?.top_pages?.length === 0 ? (
              <p className="text-[#C7CCD8] text-sm">No page views recorded yet</p>
            ) : (
              summary?.top_pages?.slice(0, 8).map((page, index) => (
                <div key={page.page} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-[#C7CCD8]/50 w-4">{index + 1}.</span>
                    <span className="text-sm text-[#C7CCD8] truncate max-w-[200px]">{page.page || '/'}</span>
                  </div>
                  <span className="text-sm font-medium text-white">{page.views} views</span>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Top Events */}
        <Card className="p-6 bg-white/5 border-[#1B2FFF]/20">
          <h3 className="font-semibold text-white mb-4">Top Events</h3>
          <div className="space-y-2">
            {summary?.top_events?.length === 0 ? (
              <p className="text-[#C7CCD8] text-sm">No events recorded yet</p>
            ) : (
              summary?.top_events?.slice(0, 8).map((event, index) => (
                <div key={event.event} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-[#C7CCD8]/50 w-4">{index + 1}.</span>
                    <span className="text-sm text-[#C7CCD8]">{formatEventType(event.event)}</span>
                  </div>
                  <span className="text-sm font-medium text-white">{event.count}</span>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Events by Type */}
      <Card className="p-6 bg-white/5 border-[#1B2FFF]/20">
        <h3 className="font-semibold text-white mb-4">Events by Type</h3>
        <div className="flex flex-wrap gap-3">
          {summary?.events_by_type && Object.entries(summary.events_by_type).map(([type, count]) => (
            <div 
              key={type} 
              className="px-4 py-2 bg-white/5 rounded-lg border border-[#1B2FFF]/20"
            >
              <span className="text-sm text-[#C7CCD8]">{formatEventType(type)}: </span>
              <span className="text-sm font-semibold text-white">{count}</span>
            </div>
          ))}
          {(!summary?.events_by_type || Object.keys(summary.events_by_type).length === 0) && (
            <p className="text-[#C7CCD8] text-sm">No events recorded yet</p>
          )}
        </div>
      </Card>

      {/* Recent Events */}
      <Card className="p-6 bg-white/5 border-[#1B2FFF]/20">
        <h3 className="font-semibold text-white mb-4">Recent Activity</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-2 text-sm font-medium text-[#C7CCD8]">Time</th>
                <th className="text-left py-3 px-2 text-sm font-medium text-[#C7CCD8]">Type</th>
                <th className="text-left py-3 px-2 text-sm font-medium text-[#C7CCD8]">Event</th>
                <th className="text-left py-3 px-2 text-sm font-medium text-[#C7CCD8]">Page</th>
              </tr>
            </thead>
            <tbody>
              {recentEvents.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-[#C7CCD8]">
                    No events recorded yet. Visit the site to generate analytics data.
                  </td>
                </tr>
              ) : (
                recentEvents.map((event) => (
                  <tr key={event.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-3 px-2 text-sm text-[#C7CCD8]">{formatTimestamp(event.timestamp)}</td>
                    <td className="py-3 px-2">
                      <span className="px-2 py-1 text-xs rounded-full bg-[#1B2FFF]/20 text-[#B8C6FF]">
                        {formatEventType(event.event_type)}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-sm text-[#C7CCD8]">{formatEventType(event.event_name)}</td>
                    <td className="py-3 px-2 text-sm text-[#C7CCD8]/70">{event.page_path || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default AdminAnalytics;
